import type { ComponentType } from "react";

import { EnergyType, type Meter } from "@eisox/api/core-api";
import { LightingIcon, type IconProps } from "@eisox/icons";

import { getLocale } from "~/paraglide/runtime";

import { ENERGIES, UNIT_SYMBOLS } from "../consumption.constant";

const unitOf = ({ unit }: Pick<Meter, "unit">): string | undefined => (unit ? UNIT_SYMBOLS[unit] : undefined);

const energyIcon = ({ energyType }: Pick<Meter, "energyType">): ComponentType<IconProps> =>
  energyType ? ENERGIES[energyType].Icon : LightingIcon;

const supportsDegreeDays = ({ energyType }: Pick<Meter, "energyType">): boolean => energyType !== EnergyType.WATER;

const DEFAULT_FRACTION_DIGITS = 1;
const SMALL_VALUE_SIGNIFICANT_DIGITS = 2;
const MAX_FRACTION_DIGITS = 6;

interface NumberFormatOptions {
  signed?: boolean;
}

/** One decimal from 1 upwards; below, two significant digits so that a small value never rounds to 0. */
const fractionDigitsOf = (value: number): number => {
  const magnitude = Math.abs(value);
  if (magnitude === 0 || magnitude >= 1) return DEFAULT_FRACTION_DIGITS;
  return Math.min(MAX_FRACTION_DIGITS, SMALL_VALUE_SIGNIFICANT_DIGITS - 1 - Math.floor(Math.log10(magnitude)));
};

const formatConsumption = (value: number, unit: string | undefined, { signed }: NumberFormatOptions = {}): string => {
  const amount = new Intl.NumberFormat(getLocale(), {
    maximumFractionDigits: fractionDigitsOf(value),
    signDisplay: signed ? "exceptZero" : "auto",
  }).format(value);
  return unit ? `${amount} ${unit}` : amount;
};

const formatAxisValue = (value: number): string =>
  new Intl.NumberFormat(getLocale(), { notation: "compact", maximumFractionDigits: MAX_FRACTION_DIGITS }).format(value);

const formatPercent = (value: number, { signed }: NumberFormatOptions = {}): string =>
  new Intl.NumberFormat(getLocale(), {
    style: "percent",
    maximumFractionDigits: 1,
    signDisplay: signed ? "exceptZero" : "auto",
  }).format(value);

export { energyIcon, formatAxisValue, formatConsumption, formatPercent, supportsDegreeDays, unitOf };
